import React, {
  useState,
  createContext,
  useEffect,
  useMemo,
  useContext,
} from "react";

import useGetEmpresa from "../hooks/useGetEmpresa";

import useGetCaixaById from "../hooks/useGetCaixaById";
import { AuthContext } from "./auth";

export const CaixaContext = createContext();

export const CaixaProvider = ({ children }) => {
  const [caixa, setCaixa] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const empresa = localStorage.getItem("empresa");
  const empresaParse = JSON.parse(empresa);
  const empresa_id = empresaParse.id_empresa;
  const empresaId = empresaParse._id;
  const vinculoEmpresa = empresaParse.cnpj;

  const { user } = useContext(AuthContext);

  const { data, refetch: refetchStatus } = useGetEmpresa(empresaId);

  const caixaId = data?.data?.empresa.caixa_aberto
    ? data?.data?.empresa.caixa_aberto_id
    : data?.data?.empresa.last_caixa_id;

  const { data: currentCaixa, refetch } = useGetCaixaById(user._id, caixaId);

  useEffect(() => {
    if (data && currentCaixa) {
      setIsLoading(true);
      let caixa_items = currentCaixa.data.empresa
        ? currentCaixa.data.empresa.lancamentos_caixa
        : [];

      setCaixa((prevCaixa) => ({
        ...prevCaixa,
        empresaId: empresaId,
        empresa_id,
        vinculoEmpresa,
        caixa_items,
        status: data.data.empresa.caixa_aberto ? "open" : "closed",
        ...currentCaixa.data.empresa,
      }));
    }
    setIsLoading(false);
  }, [data, currentCaixa, empresaId, empresa_id, vinculoEmpresa]);

  // Normaliza variações de tipo de lançamento (ex.: "cartao", "cart~o", etc.)
  const normalizedItems = useMemo(() => {
    if (!caixa.caixa_items) return [];
    return caixa.caixa_items.map((item) => {
      const raw = (item?.tipo_lancamento || "").toString().toLowerCase();
      let tipo = "";
      if (raw.includes("pix")) tipo = "Pix";
      else if (raw.includes("dinheiro") || raw.includes("cash")) tipo = "Dinheiro";
      else if (raw.includes("cart")) tipo = "Cartão"; // cobre cartao/cartão/card/cart~o
      else tipo = item?.tipo_lancamento || "";
      return { ...item, tipo_lancamento: tipo };
    });
  }, [caixa.caixa_items]);

  //"Dinheiro", "Cartão", "Pix"
  const pixValue = useMemo(() => {
    if (!normalizedItems.length) return 0;
    return normalizedItems.reduce((acc, item) => {
      if (item.tipo_lancamento === "Pix") {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [normalizedItems]);

  const cartaoValue = useMemo(() => {
    if (!normalizedItems.length) return 0;
    return normalizedItems.reduce((acc, item) => {
      if (item.tipo_lancamento === "Cartão") {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [normalizedItems]);

  const dinheiroValue = useMemo(() => {
    if (!normalizedItems.length) return caixa.saldo_inicial || 0;
    return normalizedItems.reduce((acc, item) => {
      if (item.tipo_lancamento === "Dinheiro") {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [normalizedItems, caixa.saldo_inicial]);

  const finalValue = useMemo(() => {
    if (!caixa.caixa_items) return 0;
    return caixa.caixa_items.reduce((acc, item) => acc + Number(item.valor || 0), 0);
  }, [caixa]);

  const suprimentosValue = useMemo(() => {
    if (!caixa.caixa_items) return 0;
    return caixa.caixa_items.reduce((acc, item) => {
      if (item.descricao !== "Saldo Inicial" && item.valor >= 0) {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [caixa]);

  const sangriasValue = useMemo(() => {
    if (!caixa.caixa_items) return 0;
    return caixa.caixa_items.reduce((acc, item) => {
      if (item.descricao !== "Saldo Inicial" && item.valor <= 0) {
        return acc + item.valor;
      }

      return acc;
    }, 0);
  }, [caixa]);

  const caixaValue = useMemo(() => {
    if (!caixa.caixa_items) return caixa.saldo_inicial;
    return sangriasValue + suprimentosValue + caixa.saldo_inicial;
  }, [suprimentosValue, sangriasValue, caixa]);

  return (
    <CaixaContext.Provider
      value={{
        caixa,
        setCaixa,
        finalValue,
        isLoading,
        refetch,
        suprimentosValue,
        sangriasValue,
        caixaValue,
        refetchStatus,
        dinheiroValue,
        cartaoValue,
        pixValue,
      }}
    >
      {children}
    </CaixaContext.Provider>
  );
};
